﻿import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface TabOption<T extends string | number> {
    value: T;
    label: React.ReactNode;
}

interface TabsProps<T extends string | number> {
    options: TabOption<T>[];
    value: T;
    onChange: (value: T) => void;
    className?: string;
}

export function Tabs<T extends string | number>({ options, value, onChange, className }: TabsProps<T>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [ready, setReady] = useState(false);

    const measure = useCallback(() => {
        const container = containerRef.current;
        if (!container) { return; }
        const active = container.querySelector('button[aria-selected="true"]') as HTMLElement | null;
        if (!active) { return; }
        container.style.setProperty('--tab-left', `${active.offsetLeft}px`);
        container.style.setProperty('--tab-width', `${active.offsetWidth}px`);
        container.style.setProperty('--tab-top', `${active.offsetTop}px`);
        container.style.setProperty('--tab-height', `${active.offsetHeight}px`);
    }, []);

    useEffect(() => {
        measure();
        if (!ready) { requestAnimationFrame(() => setReady(true)); }
    }, [value, ready, measure]);

    // The strip wraps when it runs out of room, so the indicator must be re-measured on resize.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) { return; }
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        return () => observer.disconnect();
    }, [measure]);

    return (
        <div ref={containerRef} className={`fe-cw-tabs${ready ? ' fe-cw-tabs--ready' : ''}${className ? ` ${className}` : ''}`}>
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    aria-selected={opt.value === value}
                    onClick={() => onChange(opt.value)}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

